import { useEffect } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { WidgetTypesProvider } from "yeati-ui";
import { setAxiosLogoutHandler } from "@/axiosConfig";
import { MovementDrawerProviderImpl } from "@/features/accounting/components/MovementDrawerProviderImpl";
import { onRemoteLogout } from "@/features/auth/services/accessToken.service";
import { logout } from "@/features/auth/store/sessionSlice";
import { ChatGlobalListener } from "@/features/chat/components/ChatGlobalListener";
import { JobProvider } from "@/features/jobs/contexts/JobProvider";
import InactivityDialog from "@/shared/components/InactivityDialog";
import { useActivityChannel } from "@/shared/hooks/useActivityChannel";
import { useActiveWidgetTypesQuery } from "@/shared/hooks/widgetTypes/useWidgetTypesQuery";
import { type AppDispatch, type RootState, store } from "@/store";
import { AdminNotificationsProvider } from "./AdminNotificationsProvider";
import { CKEditorCloudProvider } from "./CKEditorCloudContext";
import { DraftContextProvider } from "./DraftContext/DraftContextProvider";
import { FavoritesProvider } from "./FavoritesContext";

type AppProviderProps = { children: React.ReactNode };

const WidgetTypesWrapper = ({ children }: { children: React.ReactNode }) => {
  const { data, isLoading, error } = useActiveWidgetTypesQuery();

  return (
    <WidgetTypesProvider
      widgetTypes={data?.widget_types ?? []}
      isLoading={isLoading}
      error={error}
    >
      {children}
    </WidgetTypesProvider>
  );
};

const AuthenticatedContent = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.session);

  // Add activity channel hook
  const {
    showInactivityDialog,
    inactivityMessage,
    warningLevel,
    isHandlingActivity,
    countdown,
    handleCloseDialog,
  } = useActivityChannel();

  useEffect(() => {
    setAxiosLogoutHandler(() => dispatch(logout()));
  }, [dispatch]);

  // Each tab holds its access token in memory: a logout elsewhere must reach this one,
  // including for sessions Pusher does not watch (impersonation).
  // A tab on the login page keeps its form (and a 2FA in progress) as it is.
  useEffect(() => onRemoteLogout((isVoluntary) => {
    if (store.getState().session.isAuthenticated) dispatch(logout({ isVoluntary, fromOtherTab: true }));
  }), [dispatch]);

  if (!isAuthenticated) return children;

  return (
    <FavoritesProvider>
      <CKEditorCloudProvider>
        <AdminNotificationsProvider>
          <JobProvider>
            <DraftContextProvider>
              <MovementDrawerProviderImpl>
                <WidgetTypesWrapper>
                  {children}
                  <ChatGlobalListener />
                  <InactivityDialog
                    open={showInactivityDialog}
                    message={inactivityMessage}
                    warningLevel={warningLevel}
                    isHandlingActivity={isHandlingActivity}
                    countdown={countdown}
                    onClose={handleCloseDialog}
                  />
                </WidgetTypesWrapper>
              </MovementDrawerProviderImpl>
            </DraftContextProvider>
          </JobProvider>
        </AdminNotificationsProvider>
      </CKEditorCloudProvider>
    </FavoritesProvider>
  );
};

export const AppProvider = ({ children }: AppProviderProps) => {
  return (
    <Provider store={store}>
      <AuthenticatedContent>{children}</AuthenticatedContent>
    </Provider>
  );
};

export default AppProvider;
